import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Plus, Users, Calendar, BarChart3, Clock, UserCheck, AlertCircle } from 'lucide-react';
import { useRole } from '@/hooks/useRole';
import { MentorAssignmentManager } from '@/components/mentor-program/MentorAssignmentManager';
import { NewTeacherList } from '@/components/mentor-program/NewTeacherList';
import { MentorProgramReporting } from '@/components/mentor-program/MentorProgramReporting';
import { MentorComplianceTracker } from '@/components/mentor-program/MentorComplianceTracker';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface ProgramStats {
  total_assignments: number;
  active_assignments: number;
  new_teachers_count: number;
  mentors_count: number;
  compliance_rate: number;
  total_meetings_this_month: number;
  required_monthly_meetings: number;
}

export default function NewTeacherMentorProgram() {
  const { user } = useAuth();
  const { isAdmin, isCoordinator } = useRole();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState<ProgramStats>({
    total_assignments: 0,
    active_assignments: 0,
    new_teachers_count: 0,
    mentors_count: 0,
    compliance_rate: 0,
    total_meetings_this_month: 0,
    required_monthly_meetings: 0
  });
  const [loading, setLoading] = useState(true);

  const canManageProgram = isAdmin || isCoordinator;

  useEffect(() => {
    if (user) {
      loadProgramStats();
    }
  }, [user]);

  const loadProgramStats = async () => {
    try {
      // Get mentor assignments stats
      const { data: assignments, error: assignmentsError } = await supabase
        .from('mentor_assignments')
        .select('*')
        .eq('status', 'active');

      if (assignmentsError) throw assignmentsError;

      // Get profiles data for new teachers and mentors
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, is_new_teacher_program, is_mentor, teaching_experience_years');

      if (profilesError) throw profilesError;

      const newTeachers = profiles?.filter(p => p.is_new_teacher_program) || [];
      const mentors = profiles?.filter(p => p.is_mentor) || [];

      // Get this month's mentor meetings
      const thisMonth = new Date();
      thisMonth.setDate(1);
      thisMonth.setHours(0, 0, 0, 0);
      
      const { data: meetings, error: meetingsError } = await supabase
        .from('meetings')
        .select('*')
        .eq('meeting_type', 'mentoring')
        .eq('status', 'completed')
        .gte('scheduled_date', thisMonth.toISOString());

      if (meetingsError) throw meetingsError;

      const totalAssignments = assignments?.length || 0;
      const requiredMonthlyMeetings = totalAssignments * 2; // Assume 2 meetings per month per assignment
      const actualMeetings = meetings?.length || 0;
      const complianceRate = requiredMonthlyMeetings > 0 
        ? Math.round((actualMeetings / requiredMonthlyMeetings) * 100) 
        : 100;

      setStats({
        total_assignments: totalAssignments,
        active_assignments: totalAssignments,
        new_teachers_count: newTeachers.length,
        mentors_count: mentors.length,
        compliance_rate: complianceRate,
        total_meetings_this_month: actualMeetings,
        required_monthly_meetings: requiredMonthlyMeetings
      });
    } catch (error) {
      console.error('Error loading program stats:', error);
      toast.error('Failed to load mentor program statistics');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold">New Teacher Mentor Program</h1>
          <p className="text-muted-foreground mt-2">
            Manage mentor assignments and track mentoring compliance for new teacher support.
          </p>
        </div>
        
        {canManageProgram && (
          <Button onClick={() => setActiveTab('assignments')} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Manage Assignments
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Assignments</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.active_assignments}</div>
            <p className="text-xs text-muted-foreground">
              {stats.mentors_count} mentors supporting {stats.new_teachers_count} new teachers
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Meetings</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total_meetings_this_month}</div>
            <p className="text-xs text-muted-foreground">
              of {stats.required_monthly_meetings} required
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Compliance Rate</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <div className="text-2xl font-bold">{stats.compliance_rate}%</div>
              {stats.compliance_rate < 80 && (
                <AlertCircle className="h-4 w-4 text-warning" />
              )}
              {stats.compliance_rate >= 80 && (
                <UserCheck className="h-4 w-4 text-success" />
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Meeting requirements
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Program Status</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 mb-1">
              <Badge variant={stats.compliance_rate >= 80 ? "default" : "destructive"}>
                {stats.compliance_rate >= 80 ? 'Compliant' : 'At Risk'}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              Desegregation compliance
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="assignments" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Assignments
          </TabsTrigger>
          <TabsTrigger value="compliance" className="flex items-center gap-2">
            <UserCheck className="h-4 w-4" />
            Compliance
          </TabsTrigger>
          <TabsTrigger value="reports" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Reports
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <NewTeacherList onAssignmentChange={loadProgramStats} />
        </TabsContent>

        <TabsContent value="assignments" className="space-y-4">
          {canManageProgram ? (
            <MentorAssignmentManager onAssignmentChange={loadProgramStats} />
          ) : (
            <Card>
              <CardContent className="p-6">
                <p className="text-muted-foreground text-center">
                  You don't have permission to manage mentor assignments.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="compliance" className="space-y-4">
          <MentorComplianceTracker />
        </TabsContent>

        <TabsContent value="reports" className="space-y-4">
          <MentorProgramReporting />
        </TabsContent>
      </Tabs>
    </div>
  );
}